import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { TableVirtuoso } from "react-virtuoso";
import DuomenysKraunami from "../shared/DuomenysKraunami";
import {
  exportToExcelWithMultiRowHeader,
  exportToExcelWithSimpleHeader,
} from "../shared/ExportExcel";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Primary } from "../../constants/Spalvos";
import DuomenuNera from "../shared/DuomenuNera";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import LaktacijuGrafikoRodikliai from "../KarvesIstoriniaiRodikliai/LaktacijuGrafikoRodikliai";
import RodikliuPaaiskinimoDialogas from "../KarvesIstoriniaiRodikliai/RodikliuPaaiskinimoDialogas";
import InfoIcon from "@mui/icons-material/Info";
import { saveAs } from "file-saver";
import TimelineIcon from "@mui/icons-material/Timeline";
const ExcelJS = require("exceljs");

const VirtuosoTableComponents = {
  Table: (props) => (
    <Table
      {...props}
      sx={{
        borderCollapse: "separate",
        tableLayout: "fixed",
      }}
    />
  ),
  TableHead,
  TableRow: ({ item, ...props }) => {
    const style = item?.isHeader ? { backgroundColor: "#f5f5f5" } : {};
    return <TableRow {...props} style={style} />;
  },
  TableBody,
  TableCell,
};

// [x]: sutvarkyti ir grafiku etiketes pagal ekrano ismatavimus
const KarviuProduktyvumoLentele = ({
  duomenys,
  stulpeliai,
  isLoading,
  pavadinimas,
  banda,
  aukstis,
  rodikliuPaaiskinimas = false,
  laktacijuAtsisiuntimoTekstas,
  rodikliuAtsisiuntimoTekstas,
  setPasirinktaKarve = null,
  selectedCowId = null,
  mainBoxPadding = { xs: "75px", sm: "50px", md: "55px", lg: "15x", xl: "30px" },
  excelFileName = null,
  enableStickyHeader = false,
}) => {
  const [karve, setKarve] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [explainDialogOpen, setExplainDialogOpen] = useState(false);
  const [selectedLaktacijuDuomenys, setSelectedLaktacijuDuomenys] = useState([]);

  const [currentHeaderRow, setCurrentHeaderRow] = useState(null);
  const virtuosoRef = useRef(null);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleExplainDialogClose = () => {
    setExplainDialogOpen(false);
  };

  const openLaktacijuDialog = (cowId) => {
    const headerRow = duomenys.find((row) => row.isHeader && row.karve === cowId);

    if (headerRow && headerRow.lak_duom) {
      setSelectedLaktacijuDuomenys(headerRow.lak_duom);
      setKarve(cowId);
      setDialogOpen(true);
    } else {
      console.warn(`No lactation data found for cow ${cowId}`);
      // Still open dialog but with empty data
      setSelectedLaktacijuDuomenys([]);
      setKarve(cowId);
      setDialogOpen(true);
    }
  };

  // Group data by cow and track positions (only when sticky header is enabled)
  const groupedData = useMemo(() => {
    if (!enableStickyHeader) return [];

    const groups = [];
    let currentGroup = null;
    let dataRowCount = 0;

    duomenys.forEach((row, index) => {
      if (row.isHeader) {
        if (currentGroup) {
          currentGroup.endDataIndex = dataRowCount - 1;
          groups.push(currentGroup);
        }
        currentGroup = {
          headerRow: row,
          startIndex: index,
          startDataIndex: dataRowCount,
          endDataIndex: -1,
        };
      } else if (currentGroup) {
        dataRowCount++;
      }
    });

    if (currentGroup) {
      currentGroup.endDataIndex = dataRowCount - 1;
      groups.push(currentGroup);
    }

    return groups;
  }, [duomenys, enableStickyHeader]);

  // Initialize with first header (only when sticky header is enabled)
  useEffect(() => {
    if (enableStickyHeader && groupedData.length > 0 && !currentHeaderRow) {
      setCurrentHeaderRow(groupedData[0].headerRow);
    }
  }, [groupedData, currentHeaderRow, enableStickyHeader]);

  // Filter data for display (only when sticky header is enabled)
  const displayData = useMemo(() => {
    if (!enableStickyHeader) {
      return duomenys; // Return original data when sticky header is disabled
    }

    // Filter out isHeader rows since they're now shown in the sticky header
    return duomenys.filter((row) => !row.isHeader);
  }, [duomenys, enableStickyHeader]);

  // Handle scroll to update header (only when sticky header is enabled)
  const handleRangeChanged = useCallback(
    (range) => {
      if (!enableStickyHeader || !range) return;

      const { startIndex, endIndex } = range;
      const visibleRange = endIndex - startIndex + 1;

      // Count how many rows each cow has in the visible area
      const cowVisibility = new Map();

      for (let i = startIndex; i <= endIndex; i++) {
        if (i < displayData.length) {
          const row = displayData[i];
          // Find which cow this row belongs to
          const cowGroup = groupedData.find(
            (group) => i >= group.startDataIndex && i <= group.endDataIndex
          );

          if (cowGroup) {
            const cowId = cowGroup.headerRow.karve;
            cowVisibility.set(cowId, (cowVisibility.get(cowId) || 0) + 1);
          }
        }
      }

      // Find the cow with the most visible rows
      let dominantCow = null;
      let maxVisibleRows = 0;

      cowVisibility.forEach((visibleRows, cowId) => {
        if (visibleRows > maxVisibleRows) {
          maxVisibleRows = visibleRows;
          dominantCow = cowId;
        }
      });

      // Only update if we found a dominant cow and it's different from current
      if (dominantCow) {
        const dominantGroup = groupedData.find((group) => group.headerRow.karve === dominantCow);

        if (dominantGroup && dominantGroup.headerRow !== currentHeaderRow) {
          setCurrentHeaderRow(dominantGroup.headerRow);
        }
      }
    },
    [groupedData, currentHeaderRow, enableStickyHeader, displayData]
  );

  if (isLoading) {
    return <DuomenysKraunami pavadinimas={pavadinimas} />;
  }

  if (!duomenys || duomenys.length === 0) {
    return <DuomenuNera pavadinimas={pavadinimas} />;
  }

  const isMultiHeader = !!stulpeliai.nestedHeader;

  const fixedHeaderContent = () => {
    const headerContent = [];

    if (enableStickyHeader && currentHeaderRow) {
      headerContent.push(
        <TableRow key="sticky-cow-header">
          <TableCell
            colSpan={allColumns.length}
            sx={{
              padding: "8px 16px",
              fontWeight: "bold",
              textWrap: "nowrap",
              backgroundColor: selectedCowId === currentHeaderRow.karve ? "#FFFA8D" : "#e3f2fd",
              borderBottom: "2px solid #1976d2",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: "8px", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Typography variant="subtitle1">
                  Banda: <strong>{currentHeaderRow.banda}</strong>
                </Typography>
                <Typography variant="subtitle1">
                  Karvė: <strong>{currentHeaderRow.karve}</strong>
                </Typography>
                <Typography variant="subtitle1">
                  Veislė: <strong>{currentHeaderRow.veisle || "Nenurodyta"}</strong>
                </Typography>
                <Box>
                  <Tooltip title="Peržiūrėti rodiklių istoriją grafikuose">
                    <IconButton
                      onClick={() => {
                        setPasirinktaKarve(currentHeaderRow.karve);
                      }}
                      sx={{
                        color: Primary,
                        padding: 0,
                      }}
                    >
                      <Typography variant="subtitle1">Pasirinkti karvę</Typography>
                      <TimelineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box>
                <IconButton
                  onClick={() => openLaktacijuDialog(currentHeaderRow.karve)}
                  sx={{
                    color: Primary,
                    padding: 0,
                  }}
                >
                  <Typography variant="subtitle1">Laktacijų duomenys</Typography>
                  <AutoGraphIcon />
                </IconButton>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    if (!isMultiHeader) {
      headerContent.push(
        <TableRow key="main-header">
          {stulpeliai.map((col) => (
            <TableCell
              key={col.dataKey}
              align="center"
              sx={{
                width: col.width,
                backgroundColor: "background.paper",
                fontWeight: "bold",
                position: "sticky",
                top: enableStickyHeader && currentHeaderRow ? "48px" : 0, // Adjust if sticky header exists
                zIndex: 1,
              }}
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      );
    } else {
      const stickyOffset = enableStickyHeader && currentHeaderRow ? "48px" : 0;

      headerContent.push(
        <>
          {/* Pirma eile: pavadinimai */}
          <TableRow
            key="row1"
            sx={{
              lineHeight: "15px",
              maxHeight: "15px",
              height: "15px",
            }}
          >
            {stulpeliai.singleHeader.map((col) => (
              <TableCell
                key={col.dataKey}
                rowSpan={isMultiHeader ? 3 : 1}
                sx={{
                  width: col.width,
                  backgroundColor: "background.paper",
                  fontWeight: "bold",
                  lineHeight: "15px",
                  maxHeight: "15px",
                  height: "15px",
                  position: "sticky",
                  top: stickyOffset,
                  zIndex: 1,
                }}
              >
                {col.label}
              </TableCell>
            ))}

            {stulpeliai.nestedHeader.map((section) => {
              const colSpan = section.children?.reduce((acc, child) => {
                // ar yra vaiku elementu
                if (child.children) return acc + child.children.length;
                return acc + 1;
              }, 0);

              return (
                <TableCell
                  key={section.title}
                  align="center"
                  colSpan={colSpan}
                  sx={{
                    width: section.width,
                    backgroundColor: "background.paper",
                    fontWeight: "bold",
                    lineHeight: "15px",
                    maxHeight: "15px",
                    height: "15px",
                  }}
                >
                  {section.title}
                </TableCell>
              );
            })}
          </TableRow>

          {/* Second Row: group titles (if they exist) */}
          <TableRow
            key="row2"
            sx={{
              lineHeight: "15px",
              maxHeight: "15px",
              height: "15px",
            }}
          >
            {stulpeliai.nestedHeader.flatMap((section) =>
              section.children?.map((groupOrCol, i) => {
                if (groupOrCol.children) {
                  // grupuota
                  return (
                    <TableCell
                      key={`${section.title}-group-${i}`}
                      align="center"
                      colSpan={groupOrCol.children.length}
                      sx={{
                        width: groupOrCol.width,
                        backgroundColor: "background.paper",
                        fontWeight: "bold",
                        lineHeight: "15px",
                        maxHeight: "15px",
                        height: "15px",
                      }}
                    >
                      {groupOrCol.group}
                    </TableCell>
                  );
                } else {
                  return (
                    <TableCell
                      key={`${section.title}-flat-${i}`}
                      align="center"
                      rowSpan={2}
                      sx={{
                        width: section.width,
                        backgroundColor: "background.paper",
                        fontWeight: "bold",
                        lineHeight: "15px",
                        maxHeight: "15px",
                        height: "15px",
                      }}
                    >
                      {groupOrCol.label}
                    </TableCell>
                  );
                }
              })
            )}
          </TableRow>

          {/* Third Row: final column labels (only for grouped sections) */}
          <TableRow
            key="row3"
            sx={{
              lineHeight: "15px",
              maxHeight: "15px",
              height: "15px",
            }}
          >
            {stulpeliai.nestedHeader.flatMap((section) =>
              section.children?.flatMap(
                (groupOrCol) =>
                  groupOrCol.children?.map((col) => (
                    <TableCell
                      key={col.dataKey}
                      align="center"
                      sx={{
                        width: col.width,
                        backgroundColor: "background.paper",
                        fontWeight: "bold",
                        lineHeight: "15px",
                        maxHeight: "15px",
                        height: "15px",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  )) || []
              )
            )}
          </TableRow>
        </>
      );
    }

    return headerContent;
  };

  const allColumns = isMultiHeader
    ? [
        ...stulpeliai.singleHeader,
        ...stulpeliai.nestedHeader.flatMap((section) =>
          section.children.flatMap((child) => (child.children ? child.children : [child]))
        ),
      ]
    : stulpeliai;

  const itemContent = (_index, row) => {
    // When sticky header is enabled, don't render isHeader rows inline
    if (enableStickyHeader && row.isHeader) {
      return null;
    }

    // When sticky header is disabled, render isHeader rows normally
    if (!enableStickyHeader && row.isHeader) {
      return (
        <TableCell
          colSpan={allColumns.length}
          sx={{
            padding: "8px 16px",
            fontWeight: "bold",
            textWrap: "nowrap",
            backgroundColor: selectedCowId === row.karve ? "#FFFA8D" : "#f5f5f5",
          }}
        >
          <Box sx={{ display: "flex", gap: "8px", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Typography variant="subtitle1">
                Banda: <strong>{row.banda}</strong>
              </Typography>
              <Typography variant="subtitle1">
                Karvė: <strong>{row.karve}</strong>
              </Typography>
              <Typography variant="subtitle1">
                Veislė: <strong>{row.veisle || "Nenurodyta"}</strong>
              </Typography>
              <Box>
                <Tooltip title="Peržiūrėti rodiklių istoriją grafikuose">
                  <IconButton
                    onClick={() => {
                      setPasirinktaKarve(row.karve);
                    }}
                    sx={{
                      color: Primary,
                      padding: 0,
                    }}
                  >
                    <Typography variant="subtitle1">Pasirinkti karvę</Typography>
                    <TimelineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box>
              <IconButton
                onClick={() => openLaktacijuDialog(row.karve)}
                sx={{
                  color: Primary,
                  padding: 0,
                }}
              >
                <Typography variant="subtitle1">Laktacijų duomenys</Typography>
                <AutoGraphIcon />
              </IconButton>
            </Box>
          </Box>
        </TableCell>
      );
    }

    // Check if this is the first data row after an isHeader row (when sticky header is enabled)
    const isFirstRowAfterHeader =
      enableStickyHeader &&
      _index > 0 &&
      duomenys[duomenys.findIndex((item) => item === row) - 1]?.isHeader;

    const isSelectedRow = setPasirinktaKarve && selectedCowId === row.karve;

    // Regular data row rendering
    return (
      <>
        {allColumns.map((column) => {
          let displayValue =
            column.dataKey === "dab_lak_pr" || column.dataKey === "gimimo_data"
              ? row[column.dataKey]?.split("T")[0]
              : row[column.dataKey];

          let cellStyle = {};
          if (column.formatting) {
            const formattingResult = column.formatting(row[column.dataKey], row);

            if (formattingResult.color) {
              cellStyle.color = formattingResult.color;
            }

            if (formattingResult.fontWeight) {
              cellStyle.fontWeight = formattingResult.fontWeight;
            }

            if (formattingResult.text !== undefined) {
              displayValue = formattingResult.text;
            }

            if (formattingResult.style) {
              cellStyle = { ...cellStyle, ...formattingResult.style };
            }
          }

          return (
            <TableCell
              key={column.dataKey}
              sx={{
                width: column.width,
                ...cellStyle,
                borderTop:
                  isFirstRowAfterHeader && isSelectedRow
                    ? "4px solid yellow"
                    : isFirstRowAfterHeader
                    ? "4px solid #1976d2"
                    : null,
                backgroundColor:
                  isFirstRowAfterHeader && isSelectedRow
                    ? "rgb(255, 250, 141, 0.5)"
                    : isSelectedRow
                    ? "rgb(255, 250, 141, 0.25)"
                    : isFirstRowAfterHeader
                    ? "rgba(25, 118, 210, 0.05)"
                    : "inherit",
              }}
              size="small"
            >
              {displayValue}
            </TableCell>
          );
        })}
      </>
    );
  };

  const handleLaktacijuExcelExport = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Laktacijos");

    const MAX_LACTATIONS = 30;

    const row1 = worksheet.getRow(1);
    const row2 = worksheet.getRow(2);
    const row3 = worksheet.getRow(3);

    let colIndex = 1;

    // Add Karvė, Banda, Veislė headers (spanning 3 rows)
    ["Karvės nr.", "Banda", "Veislė"].forEach((header) => {
      worksheet.mergeCells(1, colIndex, 3, colIndex);
      const cell = row1.getCell(colIndex);
      cell.value = header;
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.font = { bold: true };
      colIndex++;
    });

    // First row: "Laktacijos nr." header (spans all lactation columns)
    worksheet.mergeCells(1, colIndex, 1, colIndex + (MAX_LACTATIONS * 2 - 1));
    const laktacijaHeader = row1.getCell(colIndex);
    laktacijaHeader.value = "Laktacijos nr.";
    laktacijaHeader.alignment = { horizontal: "center" };
    laktacijaHeader.font = { bold: true };

    // Second row: "Nr." headers (each spans 2 columns)
    for (let i = 1; i <= MAX_LACTATIONS; i++) {
      const nrColIndex = colIndex + (i - 1) * 2;
      worksheet.mergeCells(2, nrColIndex, 2, nrColIndex + 1);
      const cell = row2.getCell(nrColIndex);
      cell.value = i;
      cell.alignment = { horizontal: "center" };
      cell.font = { bold: true };
    }

    // Third row: "Pienas (kg)" and "R+B (kg)" for each lactation
    for (let i = 1; i <= MAX_LACTATIONS; i++) {
      const baseColIndex = colIndex + (i - 1) * 2;

      const pienasCell = row3.getCell(baseColIndex);
      pienasCell.value = "Pienas (kg)";
      pienasCell.alignment = { horizontal: "center" };
      pienasCell.font = { bold: true };

      const rbCell = row3.getCell(baseColIndex + 1);
      rbCell.value = "R+B (kg)";
      rbCell.alignment = { horizontal: "center" };
      rbCell.font = { bold: true };
    }

    // Create a map to organize lactation data by cow
    const cowLactationMap = new Map();

    // Process each cow's header row to extract lactation data
    duomenys
      .filter((row) => row.isHeader)
      .forEach((headerRow) => {
        const cowId = headerRow.karve;
        const banda = headerRow.banda;
        const veisle = headerRow.veisle;

        if (!cowLactationMap.has(cowId)) {
          cowLactationMap.set(cowId, {
            karve: cowId,
            banda: banda,
            veisle: veisle,
            laktacijos: new Map(),
          });
        }

        // Add lactation data if it exists
        if (headerRow.lak_duom && headerRow.lak_duom.length > 0) {
          headerRow.lak_duom.forEach((laktacija) => {
            const laktNr = parseInt(laktacija.nr, 10);
            if (!isNaN(laktNr) && laktNr > 0 && laktNr <= MAX_LACTATIONS) {
              cowLactationMap.get(cowId).laktacijos.set(laktNr, {
                pienas_kg: laktacija.pienas_kg,
                rb_kg: laktacija.rb_kg,
              });
            }
          });
        }
      });

    let rowIndex = 4; // Start after header rows

    cowLactationMap.forEach((cowData) => {
      const dataRow = worksheet.getRow(rowIndex);

      dataRow.getCell(1).value = cowData.karve;
      dataRow.getCell(2).value = cowData.banda;
      dataRow.getCell(3).value = cowData.veisle;

      for (let i = 1; i <= MAX_LACTATIONS; i++) {
        const baseColIndex = 4 + (i - 1) * 2;
        const laktData = cowData.laktacijos.get(i);

        if (laktData) {
          dataRow.getCell(baseColIndex).value = laktData.pienas_kg;
          dataRow.getCell(baseColIndex + 1).value = laktData.rb_kg;
        } else {
          dataRow.getCell(baseColIndex).value = "-";
          dataRow.getCell(baseColIndex + 1).value = "-";
        }
      }

      rowIndex++;
    });

    for (let i = 1; i <= rowIndex - 1; i++) {
      for (let j = 1; j <= colIndex + MAX_LACTATIONS * 2 - 1; j++) {
        const cell = worksheet.getCell(i, j);
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        if (i > 3) {
          cell.alignment = { horizontal: "center" };
        }
      }
    }

    // Auto-adjust column widths
    worksheet.columns.forEach((column) => {
      column.width = 10;
    });

    worksheet.getColumn(1).width = 15; // Karvė
    worksheet.getColumn(2).width = 12; // Banda
    worksheet.getColumn(3).width = 20; // Veislė

    // Generate filename
    let fileName = "Karviu_laktacijos";
    if (banda) fileName = `Banda_${banda}_${fileName}`;

    // If showing data for just one cow, add cow ID to filename
    if (duomenys && duomenys.length > 0) {
      const uniqueCows = new Set(duomenys.filter((row) => row.isHeader).map((row) => row.karve));
      if (uniqueCows.size === 1) {
        fileName = `Karve_${Array.from(uniqueCows)[0]}_${fileName}`;
      }
    }

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${fileName}.xlsx`);
    });
  };

  const handleExcelExport = () => {
    const dataForExcel = duomenys
      .filter((row) => !row.isHeader)
      .map((row) => {
        const excelRow = { ...row };

        if (excelRow.kontroline_data) {
          excelRow.kontroline_data = excelRow.kontroline_data?.split("T")[0];
        }

        return excelRow;
      });

    let fileName = excelFileName ? excelFileName : "Karves_istorijos_rodikliai"; // FIXME blogas failo pavadinimas produktyvumo lentelei
    // FIXME: banda gali buti viena ir visada bus naudojama, todel failo pavadinime ji visada bus, ko nereiketu
    if (banda) fileName = `Banda_${banda}_${fileName}`;

    if (duomenys && duomenys.length > 0) {
      const uniqueCows = new Set(duomenys.filter((row) => row.isHeader).map((row) => row.karve));

      if (uniqueCows.size === 1) {
        fileName = `Karve_${Array.from(uniqueCows)[0]}_${fileName}`;
      }
    }

    if (isMultiHeader) {
      exportToExcelWithMultiRowHeader({
        data: dataForExcel,
        fileName: fileName,
        multiHeaderConfig: stulpeliai,
      });
    } else {
      const excelColumns = [
        { label: "Karvė", dataKey: "karve", excelCellWidth: 15 },
        { label: "Veislė", dataKey: "veisle", excelCellWidth: 15 },
        { label: "Banda", dataKey: "banda", excelCellWidth: 12 },
        ...stulpeliai,
      ];

      exportToExcelWithSimpleHeader({
        data: dataForExcel,
        columns: excelColumns,
        fileName: fileName,
        groupByCow: true,
      });
    }
  };
  return (
    <Box
      sx={{
        height: aukstis ? aukstis : "500px",
        minHeight: "250px",
        maxHeight: "720px",
        width: "100%",
        paddingBottom: mainBoxPadding,
      }}
    >
      <Grid key="main-header-grid-container" size={12} container spacing={2} sx={{ width: "100%" }}>
        <Grid key="title-and-explain-button" size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
          <Typography sx={{ whiteSpace: "nowrap" }}>
            {pavadinimas}
            {rodikliuPaaiskinimas ? (
              <Tooltip title="Paspauskite čia, kad sužinotumėte daugiau apie rodiklius">
                <IconButton onClick={() => setExplainDialogOpen(true)}>
                  <InfoIcon color="primary" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Typography>
        </Grid>
        <Grid
          key="download-buttons"
          container
          spacing={2}
          size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
        >
          <Grid
            key="laktaciju-donwload-button"
            size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}
            paddingRight={{ xs: "0px", sm: "0px", md: "25px", lg: "15px", xl: "35px" }}
            marginLeft={{ xs: "0px", sm: "0px", md: "0px", lg: "auto", xl: "auto" }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            {laktacijuAtsisiuntimoTekstas ? (
              <IconButton onClick={handleLaktacijuExcelExport} sx={{ color: Primary, padding: 0 }}>
                <Typography>
                  {laktacijuAtsisiuntimoTekstas
                    ? laktacijuAtsisiuntimoTekstas
                    : "Atsisiųsti duomenis"}
                </Typography>
                <FileDownloadIcon />
              </IconButton>
            ) : null}
          </Grid>
          <Grid
            key="main-donwload-button"
            size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}
            sx={{ display: "flex", justifyContent: "flex-end", gap: "5px" }}
          >
            <IconButton onClick={handleExcelExport} sx={{ color: Primary, padding: 0 }}>
              <Typography>
                {rodikliuAtsisiuntimoTekstas ? rodikliuAtsisiuntimoTekstas : "Atsisiųsti duomenis"}
              </Typography>
              <FileDownloadIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <TableVirtuoso
        data={displayData}
        ref={virtuosoRef}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={itemContent}
        rangeChanged={enableStickyHeader ? handleRangeChanged : undefined} // Only add scroll handler when needed
        style={{ scrollbarWidth: "auto" }}
      />
      {rodikliuPaaiskinimas ? (
        <>
          <RodikliuPaaiskinimoDialogas
            isExplainProblemDialogOpen={explainDialogOpen}
            handleExplainProblemDialogClose={handleExplainDialogClose}
          />
        </>
      ) : null}
      <LaktacijuGrafikoRodikliai //[x] rasyti karves numeri dialoge
        isOpen={dialogOpen}
        handleDialogClose={handleDialogClose}
        karve={karve}
        laktacijuDuomenys={selectedLaktacijuDuomenys}
      />
    </Box>
  );
};

export default KarviuProduktyvumoLentele;
